import {z} from 'zod';

export const BulkUserOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
  isDisabled: z.boolean(),
});
export type BulkUserOption = z.infer<typeof BulkUserOptionSchema>;

export const BulkOperationStateSchema = z.enum([
  'PENDING',
  'IN_PROGRESS',
  'DONE',
  'PARTIALLY_DONE',
  'FAILED',
  'RESOLVE_FAILED',
  'CANCELLED',
]);

export const BulkOperationItemStateSchema = z.enum([
  'PENDING',
  'IN_PROGRESS',
  'DONE',
  'FAILED',
  'SKIPPED',
]);

export const BulkOperationResponseBodySchema = z.object({
  bulkOperationId: z.string().min(1),
  resourceId: z.string().min(1),
  actionCode: z.string().min(1),
  state: BulkOperationStateSchema,
  totalCount: z.number().int().nonnegative(),
  doneCount: z.number().int().nonnegative(),
  failedCount: z.number().int().nonnegative(),
  skippedCount: z.number().int().nonnegative(),
  createdAt: z.string(),
  finishedAt: z.string().nullable(),
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable(),
});

export const SubmitBulkOperationResponseBodySchema = z.object({
  bulkOperationId: z.string().min(1),
});

export const RerunFailedBulkOperationResponseBodySchema =
  SubmitBulkOperationResponseBodySchema.nullish();
export type RerunFailedBulkOperationResponseBody = z.infer<
  typeof RerunFailedBulkOperationResponseBodySchema
>;

export const BulkOperationItemSchema = z.object({
  recordId: z.string().min(1),
  recordLabel: z.string().nullable().optional(),
  recordAvailable: z.boolean(),
  state: BulkOperationItemStateSchema,
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable(),
  errorParams: z.record(z.string(), z.unknown()).nullable().optional(),
  processedAt: z.string().nullable().optional(),
});

export const BulkOperationItemsResponseBodySchema = z.array(BulkOperationItemSchema);

export const BulkOperationParamsSchema = z.record(z.string(), z.unknown()).nullable();

export const SubmitSelectedRecordsBulkOperationRequestBodySchema = z.object({
  recordIds: z.array(z.string().min(1)).min(1),
  params: BulkOperationParamsSchema,
});

export type BulkOperationItem = z.infer<typeof BulkOperationItemSchema>;
export type BulkOperationItemState = z.infer<typeof BulkOperationItemStateSchema>;
export type BulkOperationParams = z.infer<typeof BulkOperationParamsSchema>;
export type BulkOperationResponseBody = z.infer<typeof BulkOperationResponseBodySchema>;
export type BulkOperationState = z.infer<typeof BulkOperationStateSchema>;
export type SubmitBulkOperationResponseBody = z.infer<typeof SubmitBulkOperationResponseBodySchema>;

export type SubmitSelectedRecordsBulkOperationApiArg = {
  resourceId: string;
  actionCode: string;
  body: z.infer<typeof SubmitSelectedRecordsBulkOperationRequestBodySchema>;
};

export type ListBulkOperationsApiArg = {
  resourceId: string;
  actionCode?: string[];
  state?: BulkOperationState[];
  limit?: number;
  offset?: number;
};

export type GetBulkOperationApiArg = {bulkOperationId: string};

export type ListBulkOperationItemsApiArg = {
  bulkOperationId: string;
  state: BulkOperationItemState;
  limit?: number;
  offset?: number;
};

export type RerunFailedBulkOperationApiArg = {bulkOperationId: string};

export type CancelBulkOperationApiArg = {bulkOperationId: string};
